// PRD 11 §3.7 / §8.3 — destructive confirm before delete (Root-only,
// CONTENT_LIB:DELETE). A PUBLISHED manual adds a warning that it will be removed
// from the patient app immediately. On success, fires onDeleted so callers can
// also close an open edit panel.

import { useTranslation } from 'react-i18next';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { toast } from '@/components/feedback/useToast';

import { useDeleteManual } from '../hooks/use-manual-mutations';
import type { ConsultationManual } from '../types/counseling-manual.types';
import { getManualErrorMessage } from '../utils/error-message';

interface DeleteManualConfirmProps {
  manual: ConsultationManual | null;
  onClose: () => void;
  onDeleted?: () => void;
}

export function DeleteManualConfirm({ manual, onClose, onDeleted }: DeleteManualConfirmProps) {
  const { t } = useTranslation('counseling-manual');
  const deleteMutation = useDeleteManual();

  const handleConfirm = async () => {
    if (!manual || deleteMutation.isPending) return;
    try {
      await deleteMutation.mutateAsync(manual.cid);
      toast.success(t('toasts.deleted'));
      onClose();
      onDeleted?.();
    } catch (e) {
      toast.error(getManualErrorMessage(e, t, 'toasts.deleteError'));
    }
  };

  const message = manual
    ? manual.status === 'PUBLISHED'
      ? `${t('deleteConfirm.message', { name: manual.title || t('list.untitled') })}\n\n${t('deleteConfirm.publishedWarning')}`
      : t('deleteConfirm.message', { name: manual.title || t('list.untitled') })
    : '';

  return (
    <ConfirmModal
      open={manual !== null}
      title={t('deleteConfirm.title')}
      message={message}
      confirmLabel={t('deleteConfirm.confirm')}
      cancelLabel={t('deleteConfirm.cancel')}
      confirmVariant="destructive"
      loading={deleteMutation.isPending}
      onConfirm={handleConfirm}
      onCancel={() => {
        if (deleteMutation.isPending) return;
        onClose();
      }}
    />
  );
}
